import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import store from "../store";
import {setTemporaryNotifications} from "../notifications/actions";
import {defaultNotificationCreator} from "../../templates/Notifications";
import ordersService from "../../api/services/ordersService";

export interface IModuleSettings {
    sellerID: number;
    combinedStickersProductBarcodeCopies: number;
}

interface IOrdersReducer {
    currentOrderId: number | null;
    isOrderFormModalOpen: boolean;
    isOrdersSettingsFormOpen: boolean;
    isOrdersSettingsFormSubmitting: boolean;
    isOrderModuleSettingsSubmitting: boolean;
    isOrdersProcessingRulesSubmitting: boolean;
    isNeedUpdateData: boolean;
    stickerOrderId: number | null;
    orderExtId: string;
    marketId: string;
    moduleSettings: IModuleSettings|null;
}

const initialState: IOrdersReducer = {
    currentOrderId: null,
    isOrderFormModalOpen: false,
    isOrdersSettingsFormOpen: false,
    isOrdersSettingsFormSubmitting: false,
    isOrderModuleSettingsSubmitting: false,
    isOrdersProcessingRulesSubmitting: false,
    isNeedUpdateData: false,
    stickerOrderId: null,
    orderExtId: '',
    marketId: '',
    moduleSettings: {
        sellerID: 0,
        combinedStickersProductBarcodeCopies: 1
    }
}

const ordersStateSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        setCurrentOrderId(state, action: PayloadAction<number | null>) {
            state.currentOrderId = action.payload;
        },
        setStickerOrderId(state, action: PayloadAction<number | null>) {
            state.stickerOrderId = action.payload;
        },
        setOrderExtId(state, action: PayloadAction<string>) {
            state.orderExtId = action.payload;
        },
        setMarketId(state, action: PayloadAction<string>) {
            state.marketId = action.payload;
        },
        setIsOrderFormModalOpen(state, action: PayloadAction<boolean>) {
            state.isOrderFormModalOpen = action.payload;
        },
        setIsNeedUpdateData(state, action:  PayloadAction<boolean>) {
            state.isNeedUpdateData = action.payload;
        },
        setIsOrdersSettingsFormOpen(state, action: PayloadAction<boolean>) {
            state.isOrdersSettingsFormOpen = action.payload;
        },
        setIsOrdersSettingsFormSubmitting(state, action: PayloadAction<boolean>) {
            state.isOrdersSettingsFormSubmitting = action.payload;
        },
        setIsOrderModuleSettingsSubmitting(state, action: PayloadAction<boolean>) {
            state.isOrderModuleSettingsSubmitting = action.payload;
        },
        setIsOrdersProcessingRulesSubmitting(state, action: PayloadAction<boolean>) {
            state.isOrdersProcessingRulesSubmitting = action.payload;
        },
        setModuleSettings(state, action: PayloadAction<IModuleSettings>) {
            state.moduleSettings = action.payload;
        },
    },
});

export const saveModuleSettings = createAsyncThunk(
    'orders/saveModuleSettings',
    async (payload:IModuleSettings) => {
        try {
            const data = payload
            const res = await ordersService.updateModuleSettings(data.sellerID, data);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер при сохранении настроек заказов. status: " + res.status)
            }
            store.dispatch(setIsOrderModuleSettingsSubmitting(false))
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setIsOrderModuleSettingsSubmitting(false))
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось применить настройки заказов",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)

export const addNewOrderUpd = createAsyncThunk(
    'upd/createNew',
    async (data:any) => {
        try {
            data.amount = parseFloat(data.amount);
            console.log('add new order upd', data);
            const res = await ordersService.addNewUpdToOrder(data);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }
            store.dispatch(setIsNeedUpdateData(true))

            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Документ успешно добавлен",
                    description: "",
                    iconType: "success"
                })
            ))
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось добавить документ",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)


export const validationOrderUpd = createAsyncThunk(
    'upd/validation',
    async (data:any) => {
        try {
            data.amount = parseFloat(data.amount);
            console.log('validation order upd', data);
            const res = await ordersService.validateUpdToOrder(data);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }
            store.dispatch(setIsOrderFormModalOpen(false))
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Документ успешно добавлен",
                    description: "",
                    iconType: "success"
                })
            ))
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось добавить документ",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)

export const updateOrdersProcessingRules = createAsyncThunk(
    'ordersProcessingRules/update',
    async (payload:any) => {
        try {
            const sellerId = payload.sellerId
            const data = payload.rules
            console.log(`ordersProcessingRules ${sellerId}`, data);
            const res = await ordersService.updateSellerOrdersProcessingRules(sellerId, data);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }

            store.dispatch(setIsOrdersProcessingRulesSubmitting(false))
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setIsOrdersProcessingRulesSubmitting(false))
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось обновить правила",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)

export const updateOrdersProcessingRulesFulfillment = createAsyncThunk(
    'ordersProcessingRulesFulfillment/update',
    async (payload:any) => {
        try {
            const sellerId = payload.sellerId
            const data = payload.rules
            console.log(`ordersProcessingRulesFulfillment ${sellerId}`, data);
            const res = await ordersService.updateSellerOrdersProcessingRulesFulfillment(sellerId, data);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }

            store.dispatch(setIsOrdersSettingsFormSubmitting(false))
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Правила успешно обновлены",
                    description: "",
                    iconType: "success"
                })
            ))
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setIsOrdersSettingsFormSubmitting(false))
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось обновить правила",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)


const orders = ordersStateSlice.reducer;

export const {
    setCurrentOrderId,
    setStickerOrderId,
    setOrderExtId,
    setMarketId,
    setIsOrdersSettingsFormOpen,
    setIsOrderFormModalOpen,
    setIsOrdersSettingsFormSubmitting,
    setIsOrderModuleSettingsSubmitting,
    setIsOrdersProcessingRulesSubmitting,
    setIsNeedUpdateData,
    setModuleSettings,
} = ordersStateSlice.actions;

export default orders